import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizeImageUrl, onImageErrorSetFallback } from '../../utils/imageFallback';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Divider, LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  ShoppingCart as CartIcon,
  Download as DownloadIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  LocalOffer as OfferIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AdminAnalytics = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState(0);
  const [analytics, setAnalytics] = useState({
    dailyVisitors: [],
    behaviorDistribution: [],
    conversionFunnel: { viewers: 0, cart_adders: 0, purchasers: 0 },
    topCategories: []
  });
  const [topSearches, setTopSearches] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [remarketing, setRemarketing] = useState({ items: [], pagination: {} });
  const [remarketingType, setRemarketingType] = useState('recent');

  useEffect(() => {
    fetchData();
  }, [days]);

  useEffect(() => {
    if (tab === 2) {
      fetchRemarketing();
    }
  }, [tab, remarketingType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, searchesRes, productsRes] = await Promise.all([
        api.get(`/analytics/admin/dashboard?days=${days}`),
        api.get(`/analytics/admin/top-searches?days=${days}`),
        api.get(`/analytics/admin/top-products?days=${days}`)
      ]);
      setAnalytics(analyticsRes.data);
      setTopSearches(searchesRes.data);
      setTopProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error(t('analytics.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRemarketing = async () => {
    try {
      const response = await api.get(`/analytics/admin/remarketing?type=${remarketingType}`);
      setRemarketing(response.data);
    } catch (error) {
      console.error('Failed to fetch remarketing list:', error);
    }
  };

  const handleExportRemarketing = () => {
    window.open(`/api/analytics/admin/remarketing/export?type=${remarketingType}`, '_blank');
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('zh-TW');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('zh-TW');
  };

  // 計算轉換率
  const calcConversionRate = (from, to) => {
    if (!from || from === 0) return 0;
    return ((to / from) * 100).toFixed(1);
  };

  const { conversionFunnel } = analytics;
  const viewToCartRate = calcConversionRate(conversionFunnel.viewers, conversionFunnel.cart_adders);
  const cartToPurchaseRate = calcConversionRate(conversionFunnel.cart_adders, conversionFunnel.purchasers);
  const overallRate = calcConversionRate(conversionFunnel.viewers, conversionFunnel.purchasers);

  return (
    <Box sx={{ p: 3 }}>
      {/* 頂部標題 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon /> {t('analytics.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('analytics.time_range')}</InputLabel>
            <Select value={days} label={t('analytics.time_range')} onChange={(e) => setDays(e.target.value)}>
              <MenuItem value={1}>{t('analytics.today')}</MenuItem>
              <MenuItem value={7}>{t('analytics.last_7_days')}</MenuItem>
              <MenuItem value={30}>{t('analytics.last_30_days')}</MenuItem>
              <MenuItem value={90}>{t('analytics.last_90_days')}</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>
            {t('common.refresh')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/admin/orders')}
          >
            {t('orders.title')}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* 摘要卡片 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {analytics.dailyVisitors?.reduce((sum, d) => sum + d.visitors, 0) || 0}
                  </Typography>
                  <Typography color="text.secondary">{t('analytics.total_visitors')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ViewIcon sx={{ fontSize: 40, color: '#388e3c' }} />
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {conversionFunnel.viewers || 0}
                  </Typography>
                  <Typography color="text.secondary">{t('analytics.browsing_users')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CartIcon sx={{ fontSize: 40, color: '#f57c00' }} />
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {conversionFunnel.cart_adders || 0}
                  </Typography>
                  <Typography color="text.secondary">{t('analytics.add_to_cart')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#fce4ec' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <OfferIcon sx={{ fontSize: 40, color: '#c2185b' }} />
                  <Typography variant="h4" fontWeight="bold" color="error">
                    {conversionFunnel.purchasers || 0}
                  </Typography>
                  <Typography color="text.secondary">{t('analytics.completed_purchase')}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 分頁 */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
              <Tab icon={<TrendingUpIcon />} label={t('analytics.traffic_analysis')} />
              <Tab icon={<SearchIcon />} label={t('analytics.search_products')} />
              <Tab icon={<PeopleIcon />} label={t('analytics.remarketing_list')} />
            </Tabs>
          </Paper>

          {/* Tab 0: 流量分析 */}
          {tab === 0 && (
            <Grid container spacing={3}>
              {/* 每日訪客圖表 */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>{t('analytics.daily_visitors_trend')}</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.dailyVisitors}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(d) => {
                        if (!d) return '';
                        const date = new Date(d);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }} />
                      <YAxis />
                      <Tooltip labelFormatter={(d) => {
                        if (!d) return '';
                        const date = new Date(d);
                        return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
                      }} />
                      <Legend />
                      <Line type="monotone" dataKey="visitors" name={t('analytics.visitor_count')} stroke="#1976d2" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* 行為分布 */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>{t('analytics.behavior_distribution')}</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.behaviorDistribution}
                        dataKey="count"
                        nameKey="behavior_type"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ behavior_type, percent }) =>
                          `${behavior_type} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {analytics.behaviorDistribution.map((entry, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* 轉換漏斗 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>{t('analytics.conversion_funnel')}</Typography>
                  <Box sx={{ my: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography>{t('analytics.view_to_cart')}</Typography>
                        <Typography fontWeight="bold">{viewToCartRate}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(parseFloat(viewToCartRate), 100)}
                        sx={{ height: 10, borderRadius: 5 }}
                        color="info"
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography>{t('analytics.cart_to_purchase')}</Typography>
                        <Typography fontWeight="bold">{cartToPurchaseRate}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(parseFloat(cartToPurchaseRate), 100)}
                        sx={{ height: 10, borderRadius: 5 }}
                        color="warning"
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography>{t('analytics.overall_conversion')}</Typography>
                        <Typography fontWeight="bold" color="error">{overallRate}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(parseFloat(overallRate), 100)}
                        sx={{ height: 10, borderRadius: 5 }}
                        color="error"
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* 熱門分類 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>{t('analytics.top_categories')}</Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.topCategories} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={100} />
                      <Tooltip />
                      <Bar dataKey="views" fill="#8884d8" name={t('analytics.view_count')} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: 搜尋與商品 */}
          {tab === 1 && (
            <Grid container spacing={3}>
              {/* 熱門搜尋 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIcon /> {t('analytics.top_searches')}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('analytics.rank')}</TableCell>
                          <TableCell>{t('analytics.keyword')}</TableCell>
                          <TableCell align="right">{t('analytics.search_count')}</TableCell>
                          <TableCell align="right">{t('analytics.avg_results')}</TableCell>
                          <TableCell align="right">{t('analytics.conversions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topSearches.map((item, index) => (
                          <TableRow key={item.keyword} hover>
                            <TableCell>
                              <Chip
                                size="small"
                                label={index + 1}
                                color={index < 3 ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={index < 3 ? 'bold' : 'normal'}>
                                {item.keyword}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{item.total_searches}</TableCell>
                            <TableCell align="right">
                              {Math.round(item.avg_results) || '-'}
                            </TableCell>
                            <TableCell align="right">
                              {item.conversions || 0}
                            </TableCell>
                          </TableRow>
                        ))}
                        {topSearches.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              {t('analytics.no_search_data')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* 熱門商品 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ViewIcon /> {t('analytics.top_products')}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('analytics.product')}</TableCell>
                          <TableCell align="right">{t('analytics.views')}</TableCell>
                          <TableCell align="right">{t('analytics.add_cart')}</TableCell>
                          <TableCell align="right">{t('analytics.purchases')}</TableCell>
                          <TableCell align="right">{t('analytics.conversion_rate')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topProducts.map((item) => (
                          <TableRow key={item.product_id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    component="img"
                                    src={normalizeImageUrl(item.image_url, item.name)}
                                    alt={item.name}
                                    onError={(e) => onImageErrorSetFallback(e, item.name)}
                                    sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                                  />
                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                  {item.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{item.total_views}</TableCell>
                            <TableCell align="right">{item.total_carts}</TableCell>
                            <TableCell align="right">{item.total_purchases}</TableCell>
                            <TableCell align="right">
                              <Chip
                                size="small"
                                label={`${item.cart_rate || 0}%`}
                                color={item.cart_rate > 10 ? 'success' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                        {topProducts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              {t('analytics.no_product_data')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 2: 再行銷名單 */}
          {tab === 2 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon /> {t('analytics.remarketing_list')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t('analytics.list_type')}</InputLabel>
                    <Select
                      value={remarketingType}
                      label={t('analytics.list_type')}
                      onChange={(e) => setRemarketingType(e.target.value)}
                    >
                      <MenuItem value="recent">{t('analytics.recent_visitors')}</MenuItem>
                      <MenuItem value="abandoned">{t('analytics.abandoned_cart_users')}</MenuItem>
                      <MenuItem value="with_phone">{t('analytics.users_with_phone')}</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportRemarketing}
                  >
                    {t('analytics.export_csv')}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Session ID</TableCell>
                      <TableCell>{t('analytics.phone')}</TableCell>
                      <TableCell>{t('analytics.last_search')}</TableCell>
                      <TableCell>{t('analytics.last_viewed_product')}</TableCell>
                      <TableCell align="right">{t('analytics.visit_count')}</TableCell>
                      <TableCell align="right">{t('analytics.cart_value')}</TableCell>
                      <TableCell>{t('analytics.abandoned')}</TableCell>
                      <TableCell>{t('analytics.last_visit')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {remarketing.items?.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {item.session_id?.substring(0, 16)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.user_phone ? (
                            <Chip size="small" label={item.user_phone} color="primary" />
                          ) : '-'}
                        </TableCell>
                        <TableCell>{item.last_search || '-'}</TableCell>
                        <TableCell>
                          {item.last_product_name ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.last_product_image && (
                                <Box
                                  component="img"
                                  src={normalizeImageUrl(item.last_product_image)}
                                  alt=""
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  sx={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 1 }}
                                />
                              )}
                              <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                {item.last_product_name}
                              </Typography>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">{item.visit_count || 1}</TableCell>
                        <TableCell align="right">
                          {item.cart_value > 0 ? (
                            <Typography color="error" fontWeight="bold">
                              ${item.cart_value}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {item.cart_abandoned ? (
                            <Chip size="small" label={t('common.yes')} color="error" />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDateTime(item.last_visit_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!remarketing.items || remarketing.items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">{t('common.no_data')}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {remarketing.pagination?.total > 0 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.total_records', { count: remarketing.pagination.total })}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default AdminAnalytics;
